import { useMutation, useQueryClient } from '@tanstack/react-query';
import BadgeService from '@clickersphere/core/services/BadgeService';
import { LeadQueryData, LEADS_QUERY_KEY } from '../lead/useLeads';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { BADGES_QUERY_KEY, BadgeQueryData } from './useBadges';

export function useDeleteBadge() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const { isPending: deleteBadgeIsPending, mutateAsync: deleteBadgeAsync } =
    useMutation({
      mutationFn: async (id: string) => BadgeService.remove(id),
      onMutate: async (id) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(BADGES_QUERY_KEY),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(LEADS_QUERY_KEY),
        });

        const oldBadges = queryClient.getQueryData<BadgeQueryData[]>(
          buildQueryKey(BADGES_QUERY_KEY),
        );

        queryClient.setQueryData<BadgeQueryData[]>(
          buildQueryKey(BADGES_QUERY_KEY),
          (old) => old?.filter((badge) => badge.id !== id),
        );

        const archivedAt = new Date().toISOString();

        const oldLeadsQueries = queryClient.getQueriesData<unknown>({
          queryKey: buildQueryKey(LEADS_QUERY_KEY),
        });

        oldLeadsQueries.forEach(([queryKey, data]) => {
          if (Array.isArray(data)) {
            queryClient.setQueryData(
              queryKey,
              (data as LeadQueryData[]).map((lead) => ({
                ...lead,
                badges: (lead.badges ?? []).map((b) =>
                  b.id === id ? { ...b, deletedAt: archivedAt } : b,
                ),
              })),
            );
          } else if (data && typeof data === 'object') {
            const lead = data as LeadQueryData;
            queryClient.setQueryData(queryKey, {
              ...lead,
              badges: (lead.badges ?? []).map((b) =>
                b.id === id ? { ...b, deletedAt: archivedAt } : b,
              ),
            });
          }
        });

        return { oldBadges, oldLeadsQueries };
      },
      onError: async (_error, _id, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(BADGES_QUERY_KEY),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(LEADS_QUERY_KEY),
        });

        queryClient.setQueryData(
          buildQueryKey(BADGES_QUERY_KEY),
          context?.oldBadges,
        );

        context?.oldLeadsQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      },
    });

  return {
    deleteBadgeAsync,
    deleteBadgeIsPending,
  };
}
